import { parseJsonField } from "./parseJson";
import { getWindRoseSeriesColor } from "./sensorPlacement";
import { polygonCentroid } from "./polygon";

export const WIND_ALARM_METRICS = [
  { key: "hours_windalarm_total", label: "Wind alarm" },
  { key: "hours_windrisk", label: "Wind risk" },
  { key: "hours_alarm_without_risk", label: "Alarm without risk" },
  { key: "hours_risk_without_alarm", label: "Risk without alarm" },
];

export const DEFAULT_WIND_ALARM_METRIC = WIND_ALARM_METRICS[0].key;

export const getWindAlarmMetricLabel = (metricKey) =>
  WIND_ALARM_METRICS.find((metric) => metric.key === metricKey)?.label ||
  metricKey;

export const getWindowMetricValue = (windowItem, metricKey) =>
  Number(windowItem?.[metricKey]) || 0;

export const WIND_ROSE_SERIES_LABELS = {
  window_avg: "Window – mean",
  window_gust: "Window – gust",
  group_gust: "Zone – worst window",
  control: "Applied correction",
};

export const getWindRoseSeriesLabel = (key) =>
  WIND_ROSE_SERIES_LABELS[key] || key;

const WIND_ROSE_SERIES_ORDER = [
  "window_avg",
  "window_gust",
  "group_gust",
  "control",
];

const sortedDirectionKeys = (seriesMap = {}) => {
  const keys = new Set();
  Object.values(seriesMap).forEach((series) => {
    Object.keys(series || {}).forEach((key) => keys.add(key));
  });
  return [...keys].sort((a, b) => Number(a) - Number(b));
};

export const buildSimpleWindRoseChartData = (seriesMap = {}) => {
  const apiKeys = Object.keys(seriesMap || {}).filter(
    (name) =>
      seriesMap[name] &&
      typeof seriesMap[name] === "object" &&
      Object.keys(seriesMap[name]).length > 0,
  );

  const orderedKeys = [
    ...WIND_ROSE_SERIES_ORDER.filter((key) => apiKeys.includes(key)),
    ...apiKeys.filter((key) => !WIND_ROSE_SERIES_ORDER.includes(key)),
  ];

  const seriesNames = orderedKeys.map(getWindRoseSeriesLabel);
  const directions = sortedDirectionKeys(
    Object.fromEntries(orderedKeys.map((key) => [key, seriesMap[key]])),
  );

  const data = directions.map((direction) => {
    const row = { direction };
    orderedKeys.forEach((key) => {
      row[getWindRoseSeriesLabel(key)] = seriesMap[key]?.[direction] ?? null;
    });
    return row;
  });

  return { data, seriesNames };
};

export const seriesValuesToOrderedArray = (series = {}, directions = []) =>
  directions.map((direction) => Number(series?.[direction] ?? 0));

export const normalizeWindAlarmWindow = (id, raw = {}) => {
  const flatPositions = parseJsonField(raw.polygon, raw.polygon);
  if (
    !Array.isArray(flatPositions) ||
    typeof flatPositions[0] !== "number" ||
    flatPositions.length < 9
  ) {
    return null;
  }

  const windRoseSeries = raw.windrose || {};
  const directions = sortedDirectionKeys(windRoseSeries);

  const pickNumber = (...keys) => {
    for (const key of keys) {
      if (raw[key] != null && raw[key] !== "") return Number(raw[key]);
    }
    return null;
  };

  const hoursWindAlarm = Number(raw.hours_windalarm_total ?? 0);
  const hoursWindRisk = Number(raw.hours_windrisk ?? 0);
  const hoursAlarmWithoutRisk = Number(raw.hours_alarm_without_risk ?? 0);
  const hoursRiskWithoutAlarm = Number(raw.hours_risk_without_alarm ?? 0);
  const centroid = polygonCentroid(raw.polygon);

  return {
    name: id,
    scenario_name: raw.scenario_name,
    surface_id: raw.surface_id,
    wind_group_name: raw.wind_group_name,
    wind_group_avg_cutoff_windspeed_ms: pickNumber(
      "wind_group_avg_cutoff_windspeed_ms",
    ),
    safety_factor: pickNumber("safety_factor") ?? raw.safety_factor,
    hours_windrisk: hoursWindRisk,
    hours_windalarm_total: hoursWindAlarm,
    hours_alarm_without_risk: hoursAlarmWithoutRisk,
    hours_risk_without_alarm: hoursRiskWithoutAlarm,
    polygon: JSON.stringify(flatPositions),
    x: raw.x ?? centroid.x,
    y: raw.y ?? centroid.y,
    z: raw.z ?? centroid.z,
    windRoseSeries,
    time_velocity_more_than_12: hoursWindAlarm,
    avg: JSON.stringify(
      seriesValuesToOrderedArray(windRoseSeries.window_avg, directions),
    ),
    max: JSON.stringify(
      seriesValuesToOrderedArray(windRoseSeries.window_gust, directions),
    ),
  };
};

export const normalizeWindAlarmWindowsResponse = (payload) => {
  if (!payload) return [];
  const rows = Array.isArray(payload)
    ? payload.map((row, index) =>
        normalizeWindAlarmWindow(row?.name || row?.ID || String(index), row),
      )
    : Object.entries(payload).map(([id, row]) =>
        normalizeWindAlarmWindow(id, row),
      );
  return rows.filter(Boolean);
};

export { getWindRoseSeriesColor };
